import { useQuery } from '@tanstack/react-query'
import { controlCenterApi, type Certificate } from '@/lib/api'
import StatusBadge from '@/components/StatusBadge'
import PageHeader from '@/components/PageHeader'

type CertificatesResponse = {
  success?: boolean
  certificates?: Certificate[]
  source?: string
  configured?: boolean
  error?: string
}

const STATUS_TONE: Record<Certificate['status'], 'success' | 'warning' | 'danger' | 'neutral'> = {
  ok: 'success',
  expiring_soon: 'warning',
  expired: 'danger',
  unreadable: 'neutral',
}

const STATUS_LABEL: Record<Certificate['status'], string> = {
  ok: 'OK',
  expiring_soon: 'Expiring soon',
  expired: 'Expired',
  unreadable: 'Unreadable',
}

export default function Certificates() {
  const { data, isLoading, isError } = useQuery<CertificatesResponse>({
    queryKey: ['control-center', 'certificates'],
    queryFn: controlCenterApi.listCertificates,
  })

  const certs = data?.certificates ?? []
  const expiring = certs.filter((c) => c.status === 'expiring_soon' || c.status === 'expired').length

  return (
    <div className="p-8 space-y-6">
      <PageHeader
        title="Certificates"
        description={
          data?.configured
            ? `${certs.length} certs · ${expiring} need attention. Source: ${data.source}`
            : 'TLS certificate directory not configured.'
        }
      />

      {isError ? (
        <div className="rounded-md border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive">
          Failed to load certificates.
        </div>
      ) : null}

      {data?.error ? (
        <div className="rounded-md border border-amber-500/30 bg-amber-500/5 px-4 py-3 text-sm text-amber-400">
          {data.error}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-muted/40">
            <tr className="text-left">
              <th className="px-4 py-2.5 font-medium">Domain</th>
              <th className="px-4 py-2.5 font-medium">Issuer</th>
              <th className="px-4 py-2.5 font-medium">Expires</th>
              <th className="px-4 py-2.5 font-medium">Days left</th>
              <th className="px-4 py-2.5 font-medium">Status</th>
              <th className="px-4 py-2.5 font-medium">Path</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  Loading…
                </td>
              </tr>
            ) : certs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-muted-foreground">
                  No certificates found.
                </td>
              </tr>
            ) : (
              certs.map((c) => (
                <tr key={c.path} className="border-t border-border">
                  <td className="px-4 py-2.5 font-medium">{c.domain}</td>
                  <td className="px-4 py-2.5 text-xs text-muted-foreground font-mono truncate max-w-xs">
                    {c.issuer ?? '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs">
                    {c.expires_at ? new Date(c.expires_at).toISOString().slice(0, 10) : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-xs">{typeof c.days_remaining === 'number' ? c.days_remaining : '—'}</td>
                  <td className="px-4 py-2.5">
                    <StatusBadge status={c.status} label={STATUS_LABEL[c.status]} tone={STATUS_TONE[c.status]} />
                  </td>
                  <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground truncate max-w-md">
                    {c.path}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
